import React from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Minus, Plus, Trash2 } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { formatPrice, waLink, imgUrl } from "@/lib/api";
import { OctopusMascot } from "@/components/Mascot";

export default function CartDrawer() {
  const { cart, cartTotal, cartOpen, setCartOpen, updateQty, removeFromCart, content } = useStore();

  const checkout = () => {
    const lines = cart.map((i) => {
      const extras = [i.color && `Cor: ${i.color}`, i.variation && i.variation].filter(Boolean).join(" | ");
      const pricePart = i.price > 0 ? ` — ${formatPrice(i.price * i.qty)}` : "";
      return `- ${i.qty}x ${i.name}${extras ? ` (${extras})` : ""}${pricePart}`;
    });
    const total = cartTotal > 0 ? `\nTotal: ${formatPrice(cartTotal)}` : "";
    const msg = `Olá, Art3D! Gostaria de fazer o pedido:\n${lines.join("\n")}${total}`;
    window.open(waLink(content, msg), "_blank");
  };

  return (
    <Sheet open={cartOpen} onOpenChange={setCartOpen}>
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col p-0" data-testid="cart-drawer">
        <SheetHeader className="px-6 py-5 border-b border-slate-100">
          <SheetTitle className="text-xl font-black text-slate-900">Seu carrinho 🛒</SheetTitle>
        </SheetHeader>

        {cart.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 px-8 text-center" data-testid="cart-empty-state">
            <OctopusMascot size={110} wave className="animate-float-slower" />
            <div>
              <p className="font-bold text-slate-800 text-lg">Seu carrinho está vazio</p>
              <p className="text-sm text-slate-500 mt-1">Que tal dar uma olhada nas nossas criações em 3D?</p>
            </div>
            <a
              href="/produtos"
              onClick={() => setCartOpen(false)}
              className="bg-brand-gradient text-white font-bold px-6 py-3 rounded-full text-sm shadow-soft active:scale-95 transition-transform"
              data-testid="cart-empty-cta"
            >
              Ver produtos
            </a>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-4 flex flex-col gap-4">
              {cart.map((item) => (
                <div key={item.key} className="flex gap-3 bg-slate-50 rounded-2xl p-3" data-testid={`cart-item-${item.key}`}>
                  <div className="w-20 h-20 rounded-xl overflow-hidden bg-white shrink-0">
                    {item.image ? (
                      <img src={imgUrl(item.image)} alt={item.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-soft-gradient" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-slate-800 text-sm truncate">{item.name}</p>
                    {(item.color || item.variation) && (
                      <p className="text-xs text-slate-500">
                        {[item.color && `Cor: ${item.color}`, item.variation].filter(Boolean).join(" · ")}
                      </p>
                    )}
                    <p className="text-sm font-extrabold text-pink-600 mt-1">{formatPrice(item.price)}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <button
                        data-testid={`cart-dec-${item.key}`}
                        aria-label="Diminuir"
                        onClick={() => updateQty(item.key, item.qty - 1)}
                        className="w-7 h-7 rounded-full bg-white shadow-soft flex items-center justify-center text-slate-600 active:scale-90 transition-transform"
                      >
                        <Minus size={14} />
                      </button>
                      <span className="text-sm font-bold w-6 text-center" data-testid={`cart-qty-${item.key}`}>{item.qty}</span>
                      <button
                        data-testid={`cart-inc-${item.key}`}
                        aria-label="Aumentar"
                        onClick={() => updateQty(item.key, item.qty + 1)}
                        className="w-7 h-7 rounded-full bg-white shadow-soft flex items-center justify-center text-slate-600 active:scale-90 transition-transform"
                      >
                        <Plus size={14} />
                      </button>
                      <button
                        data-testid={`cart-remove-${item.key}`}
                        aria-label="Remover"
                        onClick={() => removeFromCart(item.key)}
                        className="ml-auto w-7 h-7 rounded-full flex items-center justify-center text-slate-400 hover:text-red-500 transition-colors"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="border-t border-slate-100 px-6 py-5 bg-white">
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm font-semibold text-slate-500">Total</span>
                <span className="text-2xl font-black text-slate-900" data-testid="cart-total">{formatPrice(cartTotal)}</span>
              </div>
              <button
                data-testid="checkout-whatsapp-btn"
                onClick={checkout}
                className="w-full bg-[#25D366] hover:bg-[#20bd5a] text-white font-bold py-3.5 rounded-full flex items-center justify-center gap-2 shadow-soft transition-all duration-200 active:scale-95"
              >
                <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current"><path d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2Zm5.4 14.1c-.2.7-1.3 1.3-1.9 1.4-.5.1-1.1.1-1.8-.1-.4-.1-1-.3-1.7-.6-3-1.3-4.9-4.3-5.1-4.5-.1-.2-1.2-1.6-1.2-3s.7-2.1 1-2.4c.2-.3.5-.3.7-.3h.5c.2 0 .4 0 .6.4l.9 2.1c.1.2.1.4 0 .6l-.4.6c-.1.2-.3.4-.1.7.1.3.7 1.1 1.5 1.8 1 .9 1.9 1.2 2.1 1.3.3.1.5.1.6-.1l.8-1c.2-.2.4-.2.6-.1l2 1c.3.1.5.2.5.4.1.1.1.6-.1 1.2Z"/></svg>
                Finalizar no WhatsApp
              </button>
              <p className="text-[11px] text-slate-400 text-center mt-3">
                O pedido é enviado como mensagem e finalizado direto com a Art3D.
              </p>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
